import React, { useMemo } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { calculateFollowUpDate, formatFollowUpPreview } from "./taskDateHelpers";

/**
 * Zahl + Zeiteinheit + Live-Vorschau des resultierenden Datums.
 * Wird vom Follow-Up-Feld und vom Verschieben-Popover der Work-Session genutzt.
 *
 * @param {number|string} amount
 * @param {string} unit - minutes | hours | business_days | weeks
 * @param {Function} onChange - ({ amount, unit }) => void
 * @param {Date|string} [baseDate] - Basis der Vorschau (Default: jetzt)
 * @param {string} [label] - Text links neben dem Feld
 */
export const DurationField = ({
  amount = 3,
  unit = "business_days",
  onChange,
  baseDate,
  label,
}) => {
  const { t } = useTranslation(["components", "common"]);

  const count = Number(amount) || 1;

  const unitOptions = useMemo(
    () => [
      {
        value: "minutes",
        label: count === 1 ? t("Minute") : t("Minutes"),
      },
      {
        value: "hours",
        label: count === 1 ? t("Hour") : t("Hours"),
      },
      {
        value: "business_days",
        label: count === 1 ? t("Business day") : t("Business days"),
      },
      {
        value: "weeks",
        label: count === 1 ? t("Week") : t("Weeks"),
      },
    ],
    [t, count]
  );

  const preview = useMemo(
    () => formatFollowUpPreview(calculateFollowUpDate(amount, unit, baseDate)),
    [amount, unit, baseDate]
  );

  const handleAmountChange = (e) => {
    const val = e.target.value;
    if (val === "") {
      onChange?.({ amount: "", unit });
      return;
    }
    const parsed = parseInt(val, 10);
    onChange?.({ amount: isNaN(parsed) ? 1 : Math.max(1, parsed), unit });
  };

  const handleAmountBlur = () => {
    if (!amount || Number(amount) < 1) onChange?.({ amount: 1, unit });
  };

  return (
    <>
      <div className="small text-muted mb-1">{label || t("Due in:")}</div>
      <Row className="align-items-center g-2">
        <Col xs={4} sm={3}>
          <Form.Control
            type="number"
            size="sm"
            min="1"
            max="999"
            value={amount}
            onChange={handleAmountChange}
            onBlur={handleAmountBlur}
          />
        </Col>
        <Col>
          <Form.Select
            size="sm"
            value={unit}
            onChange={(e) => onChange?.({ amount, unit: e.target.value })}
          >
            {unitOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
      {preview && (
        <div className="small text-muted mt-1">({preview})</div>
      )}
    </>
  );
};

/**
 * Reusable Follow-up Task field for Activity modals (Call, Meeting, etc.)
 *
 * Provides:
 * - Checkbox to toggle follow-up task creation
 * - Number input + time-unit dropdown (DurationField) with live preview
 *
 * @param {Object} value - { enabled: boolean, amount: number|string, unit: string }
 * @param {Function} onChange - Callback receiving updated value object
 * @param {string} [id="follow-up-task-checkbox"] - DOM id for checkbox
 * @param {string} [label] - Optional custom label for checkbox
 * @param {Date|string} [baseDate] - Base date for relative calculation (defaults to now)
 * @param {string} [className] - Additional wrapper css class
 */
const FollowUpTaskField = ({
  value = {},
  onChange,
  id = "follow-up-task-checkbox",
  label,
  baseDate,
  className = "",
}) => {
  const { t } = useTranslation(["components", "common"]);

  const enabled = Boolean(value.enabled);
  const amount = value.amount ?? 3;
  const unit = value.unit || "business_days";

  const handleCheckboxChange = (e) => {
    onChange?.({
      ...value,
      enabled: e.target.checked,
      amount: value.amount ?? 3,
      unit: value.unit || "business_days",
    });
  };

  return (
    <div className={`card bg-light border p-2 mb-2 ${className}`}>
      <Form.Check
        type="checkbox"
        id={id}
        label={label || t("Create follow-up task")}
        checked={enabled}
        onChange={handleCheckboxChange}
        className="fw-semibold"
      />
      {enabled && (
        <div className="mt-2">
          <DurationField
            amount={amount}
            unit={unit}
            baseDate={baseDate}
            onChange={(next) => onChange?.({ ...value, ...next })}
          />
        </div>
      )}
    </div>
  );
};

export default FollowUpTaskField;
